import { FocusMonitor } from '@angular/cdk/a11y';
import { CollectionViewer, ListRange } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { FlatTreeControl } from '@angular/cdk/tree';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ContentChildren, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Optional, Output, QueryList, Self, SimpleChanges, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { SortDirection } from '@angular/material/sort';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { PageFilterGroupType } from '@common/paged-data/enums/page-filter-group-type.enum';
import { PageFilterGroup } from '@common/paged-data/types/page-filter-group.type';
import { PageSort } from '@common/paged-data/types/page-sort.type';
import { FilterBuilder } from '@common/paged-data/util/filter-builder';
import { basename, dirname, getPathAncestry } from '@common/util/path';
import { PropertyObservable } from '@common/util/property-observable.decorator';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { MatMenuPosition } from '@portal-core/general/models/mat-menu-position.model';
import { AllFileFilter } from '@portal-core/project-files/constants/file-filters.constants';
import { ProjectFilesTreeItemDirective } from '@portal-core/project-files/directives/project-files-tree-item/project-files-tree-item.directive';
import { ProjectFileUpdateAction } from '@portal-core/project-files/enums/project-file-update-action.enum';
import { ProjectFileFilterType } from '@portal-core/project-files/enums/project-files-filter-type.enum';
import { ProjectFolder } from '@portal-core/project-files/enums/project-folder.enum';
import { TriSelectedState } from '@portal-core/project-files/enums/tri-selected-state.enum';
import { CreateProjectFileEvent } from '@portal-core/project-files/events/create-project-file.event';
import { RenameProjectFileEvent } from '@portal-core/project-files/events/rename-project-file.event';
import { ProjectFile } from '@portal-core/project-files/models/project-file.model';
import { ProjectFilesTreeIndicator } from '@portal-core/project-files/models/project-files-tree-indicator.model';
import { ProjectFilesApiService } from '@portal-core/project-files/services/project-files-api.service';
import { ProjectFilesChecklistService } from '@portal-core/project-files/services/project-files-checklist.service';
import { ProjectFilesDataProvider } from '@portal-core/project-files/services/project-files-data-provider';
import { ProjectFilesTreeControlService } from '@portal-core/project-files/services/project-files-tree-control.service';
import { ProjectFilesTreeDataProvider, ProjectFileTreeUpdateChange } from '@portal-core/project-files/services/project-files-tree-data-provider';
import { ProjectFilesTreeDataService } from '@portal-core/project-files/services/project-files-tree-data.service';
import { ProjectFilesTreeType } from '@portal-core/project-files/types/project-files-tree.type';
import { ProjectFileFlatNode } from '@portal-core/project-files/util/project-file-flat-node';
import { ProjectFileNode } from '@portal-core/project-files/util/project-file-node';
import { ProjectFilesTreeControl } from '@portal-core/project-files/util/project-files-tree-control';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { CustomInputBase } from '@portal-core/ui/forms/util/custom-input-base.directive';
import { ContextMenuItemsDirective } from '@portal-core/ui/grid/directives/context-menu-items/context-menu-items.directive';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { WorkspaceFilters } from '@portal-core/workspaces/models/workspace-filters.model';
import { WorkspacesService } from '@portal-core/workspaces/services/workspaces.service';
import { WorkspaceFileTreeConfig } from '@portal-core/workspaces/util/workspace-file-tree-config';
import { combineLatest, filter, forkJoin, map, mergeMap, Observable, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';

export interface ProjectFileContextMenuData {
  projectId: number;
  branchName: string;
  filePath: string;
  usingWorkspaceTree: boolean;
};

export interface TreeData {
  projectId: number;
  branchName: string;
}

@AutoUnsubscribe()
@Component({
  selector: 'mc-project-files-tree',
  templateUrl: './project-files-tree.component.html',
  styleUrls: ['./project-files-tree.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProjectFilesTreeDataService, ProjectFilesTreeControlService, ProjectFilesChecklistService]
})

export class ProjectFilesTreeComponent extends CustomInputBase<string[]> implements OnInit, OnChanges, OnDestroy {
  @Input() filePath: string = null;
  /** The place in the tree to scroll to on open if a user hasn't selected a value yet. */
  @Input() defaultPath: string = null;
  @Input() includeFiles: boolean = true;
  @Input() rootFolder: string = ProjectFolder.Root;
  @Input() show404ErrorAsEmptyTree: boolean = false;
  @Input() projectFilesTreeControl?: ProjectFilesTreeControl;
  @Input() projectFilesTreeType: ProjectFilesTreeType = 'default';
  @Input() ignoreInitialFilters: boolean = false;
  @Input() showIcons?: boolean = true;
  @Input() fileFilter: string = null;
  @Input() fileFilterType: ProjectFileFilterType = null;
  @Input() treeClass?: string;
  @Input() treeData: TreeData;
  @Input() disableSelectionTree: boolean = false;
  @Input() disableFolderCheckboxes: boolean = false;
  @Input() committedFilter: boolean = null;
  @Input() showIndicators: boolean = true;

  @Output() createFile: EventEmitter<CreateProjectFileEvent> = new EventEmitter<CreateProjectFileEvent>();
  @Output() fileDeleted: EventEmitter<string> = new EventEmitter<string>();
  @Output() fileRenamed: EventEmitter<RenameProjectFileEvent> = new EventEmitter<RenameProjectFileEvent>();
  @Output() fileSelected: EventEmitter<ProjectFileFlatNode> = new EventEmitter<ProjectFileFlatNode>();
  @Output() pathFiltersChanged: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() workspaceFileFiltersChanged: EventEmitter<WorkspaceFilters> = new EventEmitter<WorkspaceFilters>();

  @PropertyObservable('treeData') treeData$: Observable<TreeData>;
  @PropertyObservable('fileFilter') fileFilter$: Observable<string>;
  @InputObservable('projectId') projectId$: Observable<number>;
  @PropertyObservable('committedFilter') committedFilter$: Observable<boolean>;
  @PropertyObservable('treeClass') treeClass$: Observable<string>;
  @PropertyObservable('projectFilesTreeType') projectFilesTreeType$: Observable<ProjectFilesTreeType>;

  @ContentChild(ContextMenuItemsDirective) contextMenuItemsDirective: ContextMenuItemsDirective;
  @ContentChildren(ProjectFilesTreeItemDirective) treeItemDirectives: QueryList<ProjectFilesTreeItemDirective>;
  @ViewChild('viewport', { static: false }) viewPortRef: CdkVirtualScrollViewport;
  @ViewChild(MatMenuTrigger, { static: true }) contextMenu: MatMenuTrigger;
  @ViewChild('filesTreeContainer', { static: true }) filesTreeContainerRef: ElementRef<HTMLInputElement>;

  TriSelectedState: typeof TriSelectedState = TriSelectedState;

  /** Required by CustomInputBase */
  controlType: string = 'mc-project-files-tree-input';
  projectId: number;
  branchName: string;
  contextMenuItemsTemplate: TemplateRef<any>;
  contextMenuPosition: MatMenuPosition = { x: '0px', y: '0px' };
  dataSource: MatTreeFlatDataSource<ProjectFileNode, ProjectFileFlatNode>;
  emptyTree: boolean;
  filterConfig: WorkspaceFileTreeConfig = new WorkspaceFileTreeConfig();
  selectedDirectoryPath: string;
  /** This fake tree control is only needed for mat-tree styles to be included in the template. */
  fakeTreeControl: FlatTreeControl<void> = new FlatTreeControl<void>(null, null);
  loadingState: LoadingState<string> = new LoadingState<string>();
  projectFile: ProjectFile;
  treeClasses$: Observable<string[]>;
  treeControl: FlatTreeControl<ProjectFileFlatNode>;
  treeFlattener: MatTreeFlattener<ProjectFileNode, ProjectFileFlatNode>;
  firstTreeLoad: boolean = true;
  pathFilterChangedByUser: boolean = false;
  contentEditorPermissionsError: string;
  contentEditorPermissionsErrorSubscription: Subscription;

  private unsubscribe = new Subject<void>();

  /**  exposes a view over the data provided by a data source. In this case, it shows the visible nodes in the tree, so it was easy to find the index to scroll to programmatically.
   *   @param viewChange A stream that emits whenever the CollectionViewer starts looking at a new portion of the data. The start index is inclusive, while the end is exclusive.
  . */
  private dataSourceViewer: CollectionViewer = {
    viewChange: new Observable<ListRange>()
  };
  expandedNodeList: ProjectFileFlatNode[] = [];

  get treeItemFileDirective(): ProjectFilesTreeItemDirective {
    return this.treeItemDirectives?.find(treeItemDirective => treeItemDirective.templateName === 'file');
  }

  get treeItemFolderDirective(): ProjectFilesTreeItemDirective {
    return this.treeItemDirectives?.find(treeItemDirective => treeItemDirective.templateName === 'folder');
  }

  /** The data provider for tree content. If a parent component provides ProjectFilesTreeDataProvider, it is used. Otherwise defaults to ProjectFilesDataProvider (git-only). */
  private dataProvider: ProjectFilesTreeDataProvider;
  /** True when this tree constructed its own data provider. False when the provider was injected from an ancestor. */
  private ownsDataProvider: boolean;

  constructor(
    private projectsService: ProjectsService,
    private projectFilesTreeDataService: ProjectFilesTreeDataService,
    private projectFilesTreeControlService: ProjectFilesTreeControlService,
    private projectFilesApiService: ProjectFilesApiService,
    private errorService: ErrorService,
    private projectFilesChecklistService: ProjectFilesChecklistService,
    private workspacesService: WorkspacesService,
    @Optional() injectedDataProvider: ProjectFilesTreeDataProvider,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    @Optional() public _parentForm: NgForm,
    @Optional() public _parentFormGroup: FormGroupDirective,
    @Optional() @Self() public ngControl: NgControl,
    protected cdr: ChangeDetectorRef,
    protected focusMonitor: FocusMonitor
  ) {
    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl, cdr, focusMonitor);
    this.ownsDataProvider = !injectedDataProvider;
    this.dataProvider = injectedDataProvider ?? new ProjectFilesDataProvider(this.projectFilesApiService);
  }

  ngOnInit() {
    this.contentEditorPermissionsErrorSubscription = this.projectId$.pipe(
      switchMap((projectId) =>
        this.workspacesService.getAccessErrorMessages$(projectId).pipe(map(accessErrorMessages => accessErrorMessages?.contentEditorPermissionsError))
      )
    ).subscribe(errorMessage => this.contentEditorPermissionsError = errorMessage);

    this.treeClasses$ = combineLatest([
      this.treeClass$,
      this.projectFilesTreeType$
    ]).pipe(
      map(([treeClass, projectFilesTreeType]) => {
        const classes: string[] = [];

        if (treeClass) {
          classes.push(treeClass);
        }
        if (projectFilesTreeType === 'selection') {
          classes.push('mc-selection-tree');
        }

        return classes;
      })
    );

    this.projectFilesTreeControlService.pathFilters = !this.value?.length ? [''] : this.value;
    this.projectFilesTreeDataService.includeFiles = this.includeFiles;
    this.projectFilesTreeDataService.fileFilter = this.fileFilter;
    this.projectFilesTreeDataService.dataProvider = this.dataProvider;

    this.treeFlattener = new MatTreeFlattener(this.projectFilesTreeControlService.transformer.bind(this.projectFilesTreeControlService),
      this.projectFilesTreeControlService.getLevel, this.projectFilesTreeControlService.isExpandable, this.projectFilesTreeControlService.getChildren);
    this.treeControl = new FlatTreeControl<ProjectFileFlatNode>(this.projectFilesTreeControlService.getLevel, this.projectFilesTreeControlService.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.projectFilesTreeDataService.manuallyEditedTreeData.pipe(takeUntil(this.unsubscribe)).subscribe(fileEdited => {
      if (fileEdited?.action === ProjectFileUpdateAction.Add) {
        this.updateFileTree(this.dataSource.data, fileEdited.filePath, ProjectFileUpdateAction.Add, fileEdited.addNode);
      } else if (fileEdited?.action === ProjectFileUpdateAction.Remove) {
        this.updateFileTree(this.dataSource.data, fileEdited.filePath, ProjectFileUpdateAction.Remove);
      }
    })
    this.projectFilesTreeDataService.dataChange.pipe(takeUntil(this.unsubscribe)).subscribe(data => {
      // this function is called only on the first load (or reload) of this tree.
      if (!this.dataSource.data?.length && data.length) {
        this.projectFilesTreeDataService.projectId = this.projectId;
        this.firstTreeLoad = false;
        // needs a timeout because collab files might not be ready
        setTimeout(() => this.scrollToPath(this.filePath ?? this.defaultPath), 0);
      } else {
        this.loadingState.update(false);
      }
      this.dataSource.data = data;
    });
    this.projectFilesTreeDataService.toggleTreeNode.pipe(takeUntil(this.unsubscribe)).subscribe((flatNodeToToggle) => {
      this.treeControl.toggle(flatNodeToToggle);
    });
    this.treeData$.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.projectId = this.treeData?.projectId;
      this.branchName = this.treeData?.branchName;
      this.loadProjectFileRootTree();
    });

    // Subscribe to real-time file updates from the data provider (e.g. WebSocket events from workspace)
    this.dataProvider.fileUpdates$.pipe(
      filter(update => !!update),
      takeUntil(this.unsubscribe)
    ).subscribe(update => {
      update.added.forEach(addition => this.addFile(addition.filePath, addition.isUncommitted, addition.isCollabFile));
      update.removed.forEach(filePath => this.removeFile(filePath));
      update.updated.forEach(change => this.applyFileUpdate(change));
    });

    // get the visible nodes at all times so it was easier to figure out what to scroll to.
    this.dataSource.connect(this.dataSourceViewer).pipe(takeUntil(this.unsubscribe)).subscribe(data => {
      this.expandedNodeList = data;
    });

    this.fileFilter$.pipe(takeUntil(this.unsubscribe)).subscribe(fileFilter => this.projectFilesTreeDataService.fileFilter = fileFilter);
    this.committedFilter$.pipe(takeUntil(this.unsubscribe)).subscribe(committedFilter => this.projectFilesTreeDataService.committedFilter = committedFilter);
  }

  ngAfterContentInit() {
    this.contextMenuItemsTemplate = this.contextMenuItemsDirective && this.contextMenuItemsDirective.templateRef || null;
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (changes.value && !changes.value.firstChange && !this.pathFilterChangedByUser) {
      this.projectFilesTreeDataService.setTreeSelection(this.value);
    }
    this.pathFilterChangedByUser = false;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.ownsDataProvider) {
      this.dataProvider.dispose();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /** Returns true if the project files tree input is empty. It is considered empty if the value's first value is ''. */
  get empty(): boolean {
    return this.value?.[0] === '';
  }

  /** Required by MatFormFieldControl */
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  /** Required by CustomInputBase */
  getDefaultPlaceholder(): string {
    return '';
  }

  /** Required by CustomInputBase */
  getFocusableElementRef(): ElementRef<HTMLElement> {
    return this.filesTreeContainerRef;
  }

  // only works if the path is AT LEAST one folder down.
  findFolderParentNode(pathSegments: string[], projectFileNodes: ProjectFileNode[]): ProjectFileNode {
    let accumulatedPath: string = '';
    let folderNode: ProjectFileNode;
    for (let i = 0; i < pathSegments.length - 1; i++) {
      accumulatedPath = accumulatedPath ? `${accumulatedPath}/${pathSegments[i]}` : pathSegments[i];
      const index = projectFileNodes.findIndex(node => node.path === accumulatedPath);
      // not found - error
      if (index === -1) {
        return;
      }
      folderNode = projectFileNodes[index];
      projectFileNodes = projectFileNodes[index].children;
    }
    // found the folder node
    return folderNode;
  }

  updateFileTree(projectFileNodes: ProjectFileNode[], filePath: string, action: ProjectFileUpdateAction, nodeToAdd: ProjectFileNode = null) {
    const pathSegments: string[] = filePath.split('/');
    let projectFileNodeFolderChildren: ProjectFileNode[];

    // find the current children in a folder
    if (pathSegments?.length === 1) {
      projectFileNodeFolderChildren = projectFileNodes;
    } else if (pathSegments?.length > 1) {

      projectFileNodeFolderChildren = this.findFolderParentNode(pathSegments, projectFileNodes)?.children;
      if (!projectFileNodeFolderChildren) {
        return;
      }
    }

    switch (action) {
      case ProjectFileUpdateAction.Add:
        if (projectFileNodeFolderChildren && nodeToAdd) {
          projectFileNodeFolderChildren.push(nodeToAdd);
          //the node added is not in the correct order - this sort fixes that
          projectFileNodeFolderChildren.sort((a, b) => {
            // folders first
            if (a.hasChildren !== b.hasChildren) {
              return a.hasChildren ? -1 : 1
            }
            return a.path.localeCompare(b.path);
          });
        }
        break;

      case ProjectFileUpdateAction.Remove:
        const indexToDelete = projectFileNodeFolderChildren.findIndex(node => node.path === filePath);
        if (indexToDelete > -1) {
          projectFileNodeFolderChildren.splice(indexToDelete, 1);
        }
        break;
    }
    this.dataSource.data = projectFileNodes;
  }

  /** find the visual indicators (e.g. colored dots) for a file */
  findIndicator(nodePath: string): ProjectFilesTreeIndicator[] {
    if (!this.showIndicators) {
      return [];
    }
    const node = this.projectFilesTreeDataService.getNode(nodePath);
    if (node?.isUncommitted) {
      return [{ name: 'dot', color: 'mc-bg-info-2' }];
    }
    return [];
  }

  /** Sets the value and path properties on the file path  picker and notifies Angular Forms of the changes.  */
  private setValueFromUI(value: string[]) {
    this.value = value;
    this.onChange(this.value); // So Angular Forms know this control's value has changed
    this.onTouched(); // So Angular Forms know this control has been touched
    this.projectFilesTreeControlService.pathFilters = this.value;
  }

  onContextMenu(event: MouseEvent, node: ProjectFileFlatNode) {
    if (this.projectFilesTreeType === 'link' && (node.expandable && !this.contentEditorPermissionsError || !node.expandable)) {
      event.preventDefault();
      this.contextMenuPosition.x = event.clientX + 'px';
      this.contextMenuPosition.y = event.clientY + 'px';
      this.contextMenu.menuData = {
        node,
        uncommitted: this.projectFilesTreeDataService.getNode(node.path)?.isUncommitted ?? false
      };
      this.contextMenu.openMenu();
    }
  }

  onPathFiltersChanged(pathFilters: string[]) {
    this.ignoreInitialFilters = false;
    this.pathFiltersChanged.emit(pathFilters);
    this.setValueFromUI(pathFilters);
  }

  onFileSelected(node: ProjectFileFlatNode) {
    // Update UI to show file selected
    this.filePath = node.path;
    this.fileSelected.emit(node);
  }

  onRetryLoadNodes() {
    // If true sets the UI to have the right selected path
    this.loadProjectFileRootTree();
  }

  // if one of the root nodes is indeterminate or selected then there is at least one selected node.
  atLeastOneNodeSelected(): boolean {
    for (let i = 0; i < this.dataSource.data.length; i++) {
      const flatFileNode = this.dataSource.data[i].flatFileNode;
      if (flatFileNode.selected === TriSelectedState.Selected || flatFileNode.selected === TriSelectedState.Indeterminate) {
        return true;
      }
    }
    return false;
  }

  checkViewportSize() {
    this.viewPortRef?.checkViewportSize();
  }

  getPathFilters(): string[] {
    return this.value;
  }

  hardReload() {
    this.loadProjectFileRootTree();
  }

  scrollToTop() {
    if (!this.emptyTree) {
      this.viewPortRef?.scrollTo({ top: 0, behavior: 'auto' });
      this.viewPortRef?.checkViewportSize();
    }

  }

  private clearData() {
    if (this.dataSource) this.dataSource.data = [];
    this.projectFilesTreeDataService.nodeMap.clear();
    this.projectFilesTreeControlService.nodeMap.clear();
  }

  private createActivePathsForAPI(filePathParts: string[]): string[] {
    const apiPaths: string[] = [];
    let filePathDirectoryStr: string = '';

    // last one is file and is not needed
    for (let i = 0; i < filePathParts.length - 1; i++) {
      filePathDirectoryStr += filePathParts[i] + '/';
      apiPaths.push(filePathDirectoryStr.substr(0, filePathDirectoryStr.length - 1));
    }

    if (this.rootFolder) {
      apiPaths.splice(0, this.rootFolder.split('/').length);
    }
    return apiPaths;
  }

  removeFile(filePath: string): boolean {
    const node = this.projectFilesTreeDataService.getNode(filePath);
    if (!node) {
      return false;
    }
    const parent = node.parentNode;
    this.projectFilesTreeDataService.removeNode(filePath);
    if (this.updateAncestorIsUncommitted(parent)) {
      this.cdr.markForCheck();
    }
    return true;
  }

  /**
   * Ensure the given file (and any missing ancestor folders) is present in the tree with the
   * given flags applied to the leaf. Ancestor folders inherit `isUncommitted` from the leaf
   * (so a folder containing an uncommitted descendant shows the dot), but existing ancestors
   * are never cleared — they may contain other uncommitted descendants not known here.
   * `isCollabFile` only applies to the leaf; folders are never collab files.
   */
  addFile(leafPath: string, isUncommitted: boolean, isCollabFile: boolean): string[] {
    // don't allow adding nodes if the file doesn't match the filter criteria
    if (this.fileFilter && !new RegExp(this.fileFilter).test(leafPath)) {
      return [];
    }
    const ancestry = getPathAncestry(leafPath);
    const filePathsAdded: string[] = [];
    let mutatedExisting = false;

    for (let i = 0; i < ancestry.length; i++) {
      const path = ancestry[i];
      const isLeaf = i === ancestry.length - 1;
      const existing = this.projectFilesTreeDataService.getNode(path);

      if (existing) {
        // Node is already in the tree — only ever set isUncommitted to true (never clear it),
        // so folders containing other uncommitted descendants keep their dot.
        const nextIsUncommitted = isLeaf ? isUncommitted : (existing.isUncommitted || isUncommitted);
        if (existing.isUncommitted !== nextIsUncommitted) {
          existing.isUncommitted = nextIsUncommitted;
          mutatedExisting = true;
        }
        continue;
      }

      // New node: ancestor folders inherit the leaf's isUncommitted so the dot renders immediately.
      // Only the leaf can be a collab file.
      const newNode = this.createNode(path, isLeaf, isUncommitted, isLeaf && isCollabFile);
      // only load in files with folders that have already been loaded by the api
      if (newNode.parentNode?.flatFileNode?.loaded || !newNode.parentNode) {
        this.projectFilesTreeControlService.addNode(path, newNode?.flatFileNode);
        this.projectFilesTreeDataService.addNode(path, newNode);
        filePathsAdded.push(path);
      } else {
        break;
      }
    }

    // OnPush change detection does not notice in-place flag mutations on existing nodes.
    // New-node creation triggers CD via the dataSource.data reassignment in updateFileTree().
    if (mutatedExisting) {
      this.cdr.markForCheck();
    }

    return filePathsAdded;
  }

  /**
   * Apply a mutation to an existing tree node. Returns true if anything changed.
   *
   * Rules:
   * - Missing node: no-op.
   * - `isCollabFile` is only applied to the leaf (folders are never collab files).
   * - `isUncommitted: true` on a leaf propagates up the ancestor chain (OR-accumulate) so
   *   folders containing the leaf show the dot.
   * - `isUncommitted: false` on a leaf walks ancestors upward and clears each one only when
   *   no remaining descendant is still uncommitted; the walk stops at the first ancestor
   *   that must keep its dot (higher ancestors keep theirs too).
   */
  applyFileUpdate(change: ProjectFileTreeUpdateChange): boolean {
    const leaf = this.projectFilesTreeDataService.getNode(change.filePath);
    if (!leaf) {
      return false;
    }

    let mutated = false;

    if (change.isCollabFile !== undefined && !leaf.hasChildren && leaf.isCollabFile !== change.isCollabFile) {
      leaf.isCollabFile = change.isCollabFile;
      mutated = true;
    }

    if (change.isUncommitted !== undefined) {
      if (leaf.isUncommitted !== change.isUncommitted) {
        leaf.isUncommitted = change.isUncommitted;
        mutated = true;
      }

      if (change.isUncommitted) {
        // Set-true: OR-accumulate up the ancestor chain so folder dots appear.
        let ancestor = leaf.parentNode;
        while (ancestor && !ancestor.isUncommitted) {
          ancestor.isUncommitted = true;
          mutated = true;
          ancestor = ancestor.parentNode;
        }
      } else if (this.updateAncestorIsUncommitted(leaf.parentNode)) {
        mutated = true;
      }
    }

    if (mutated) {
      this.cdr.markForCheck();
    }

    return mutated;
  }

  /**
   * Walk up the ancestor chain starting at `fromAncestor`, clearing each ancestor's `isUncommitted`
   * flag when the data provider confirms no descendant is still uncommitted. Stops at the first
   * ancestor that must keep its dot (a sibling subtree still has an uncommitted file); higher
   * ancestors necessarily keep their dot for the same reason.
   *
   * Does NOT call `markForCheck` — callers decide whether to trigger change detection so they can
   * combine this mutation signal with their own.
   *
   * Returns true if any ancestor was cleared.
   */
  private updateAncestorIsUncommitted(fromAncestor: ProjectFileNode | null): boolean {
    let ancestor = fromAncestor;
    let mutated = false;
    while (ancestor && ancestor.isUncommitted && !this.dataProvider.hasUncommittedDescendant(ancestor.path)) {
      ancestor.isUncommitted = false;
      mutated = true;
      ancestor = ancestor.parentNode;
    }
    return mutated;
  }

  private createNode(filePath: string, nodeIsFile: boolean, isUncommitted: boolean, isCollabFile: boolean): ProjectFileNode {
    const parentNode = this.projectFilesTreeDataService.getNode(dirname(filePath));
    const treeNodeFlat = new ProjectFileFlatNode(basename(filePath), 0, filePath, filePath.split('/').length - 1, !nodeIsFile);
    const treeNode = new ProjectFileNode(basename(filePath), 0, filePath, treeNodeFlat, parentNode, !nodeIsFile, isCollabFile, isUncommitted);
    if (parentNode) {
      parentNode.hasChildren = true;
    }
    return treeNode;
  }

  scrollToPath(filePath: string) {
    // If the tree is in an error state with no data, there's nothing to scroll to
    if (this.loadingState.generalError$.getValue() && !this.dataSource.data?.length) {
      return;
    }

    const filePathParts = filePath ? filePath.split('/') : []
    const apiPaths = this.createActivePathsForAPI(filePathParts ?? []);
    // this function is sometimes called outside of how the tree normally loads, so when that happens the loader should hide the data appearing.
    this.loadingState.update(true);

    // if path already exist just open folders and scroll to it.
    if (filePath && this.expandPath(apiPaths)) {
      this.loadingState.update(false);
      this.scrollViewportToPath(filePath);
      return;
    }

    // load missing paths.
    if (!apiPaths.length || this.ignoreInitialFilters) {
      this.loadingState.update(false);
      return;
    } else {
      const pathChildren$ = of(apiPaths).pipe(mergeMap(paths => forkJoin(paths.map(path => this.projectFilesTreeDataService.getChildren$(path, this.branchName)))));
      pathChildren$.pipe(takeUntil(this.unsubscribe)).subscribe((nodeChildrenCollection: [ProjectFileNode[]]) => {
        for (let i = 0; i < apiPaths.length; i++) {
          if (!nodeChildrenCollection[i]) {
            continue;
          }
          const parent = this.projectFilesTreeDataService.getNode(apiPaths[i]);
          if (!parent) break; // this might happen if the root folder is changed but the active path points to a different root folder
          parent.childrenChange.next(nodeChildrenCollection[i]);
          this.projectFilesTreeDataService.dataChange.next(this.projectFilesTreeDataService.dataChange.value);
          nodeChildrenCollection[i].forEach(childNode => { childNode.parentNode = parent; this.projectFilesChecklistService.setSelection(childNode, this.value); });
          parent.flatFileNode.loaded = true;
        }
        this.loadingState.update(false);
        // open folders and scroll to active file/folder
        this.expandPath(apiPaths);
        this.scrollViewportToPath(filePath);
      });
    }
  }

  private expandPath(pathParts: string[]): boolean {
    for (let i = 0; i < pathParts.length; i++) {
      const node = this.projectFilesTreeDataService.getNode(pathParts[i]);
      if (!node || (!node.flatFileNode.loaded && !node.children?.length)) {
        return false;
      }
      this.treeControl.expand(node.flatFileNode);
    }
    return true;
  }

  private scrollViewportToPath(path: string) {
    // grab the first substring in the path that is found in the tree
    let currentSubStr = path;
    while (currentSubStr) {
      const indexToPath = this.expandedNodeList.findIndex(node => node.path === currentSubStr);
      if (indexToPath >= 0) {
        // Calculate the actually visible index range from scroll position and viewport size.
        // getRenderedRange() includes buffered items outside the viewport, so we can't use it.
        const itemSize = 48; // matches itemSize on cdk-virtual-scroll-viewport in the template
        const scrollOffset = this.viewPortRef.measureScrollOffset();
        const viewportSize = this.viewPortRef.getViewportSize();
        const firstVisibleIndex = Math.ceil(scrollOffset / itemSize);
        const lastVisibleIndex = Math.floor((scrollOffset + viewportSize) / itemSize) - 1;

        if (indexToPath < firstVisibleIndex || indexToPath > lastVisibleIndex) {
          // Scroll so the target item is centered in the viewport
          const visibleCount = Math.floor(viewportSize / itemSize);
          const centeredIndex = Math.max(0, indexToPath - Math.floor(visibleCount / 2));
          setTimeout(() => this.viewPortRef.scrollToIndex(centeredIndex), 0);
        }
        return;
      }
      const lastIndexOfFolder = currentSubStr.lastIndexOf('/');
      currentSubStr = currentSubStr.substring(0, lastIndexOfFolder);
    }
  }

  private loadProjectFileRootTree() {
    this.clearData();
    this.emptyTree = true;
    if (typeof this.projectId === 'number' && typeof this.branchName === 'string') {
      this.loadingState.update(true);

      this.dataProvider.getTree$({
        projectId: this.projectId,
        branchName: this.branchName,
        treePath: this.rootFolder,
        includeFiles: this.includeFiles,
        fileFilter: this.fileFilter,
        committedFilter: this.committedFilter
      }).pipe(takeUntil(this.unsubscribe)).subscribe(projectFiles => {
        if (Array.isArray(projectFiles) && projectFiles.length > 0) {
          this.emptyTree = false;
          const nodes = this.projectFilesTreeDataService.initialize(projectFiles);
          this.projectFilesTreeDataService.setTree(nodes, this.value, this.ignoreInitialFilters);
        } else {
          this.loadingState.update(false);
        }
      }, error => {
        if (this.show404ErrorAsEmptyTree && error.status === 404) {
          this.loadingState.update(false);
        } else {
          this.emptyTree = false;
          this.loadingState.update(false, 'Unable to load the file tree.', this.errorService.getErrorMessages(error));
        }
      });
    }
  }

  changeRootFolder(newRootDirectory: string, newFileFilter?: string) {
    this.rootFolder = newRootDirectory ? newRootDirectory : ProjectFolder.Root;
    if (newFileFilter)
      this.fileFilter = newFileFilter ? newFileFilter : AllFileFilter;
    this.loadProjectFileRootTree();
  }

  selectedDirectoryPathChanged(newDirectoryPath: string) {
    this.selectedDirectoryPath = newDirectoryPath;
  }

  nodeToggled(node: ProjectFileFlatNode) {
    this.treeControl.toggle(node);
  }

  findNodeByPathAndUnselect(path: string, pathFilters: string[]) {
    this.setValueFromUI(pathFilters);
    const node = this.projectFilesTreeDataService.getNode(path);
    if (node) {
      node.flatFileNode.selected = TriSelectedState.NotSelected;
      this.cdr.markForCheck();
    }
  }

  setSelection() {
    this.projectFilesTreeDataService.setTreeSelection(this.value);
  }

  applyFilter$(name: string, filter: PageFilterGroup): Observable<any> {
    if (name === 'file-status') {
      this.workspaceFileFiltersChanged.emit({ fileStatusFilter: true, fileTypeFilter: this.fileFilterType });
    } else {
      this.workspaceFileFiltersChanged.emit({ fileStatusFilter: this.committedFilter, fileTypeFilter: null });
    }
    return of(null);
  }

  getFilter(name: string): PageFilterGroup {
    const filters = this.getFilters(this.fileFilter, this.committedFilter);
    if (name === 'file-status') {
      return filters['file-status'];
    } else {
      return filters['file-type'];
    }

  }

  getFilters$(): Observable<Dictionary<PageFilterGroup>> {
    return combineLatest([
      this.fileFilter$,
      this.committedFilter$
    ]).pipe(map(([fileFilter, committedFilter]) => {
      return this.getFilters(fileFilter, committedFilter);
    }))
  }

  // helper function
  getFilters(fileFilter: string, committedFilter: boolean): Dictionary<PageFilterGroup> {
    let allFilters: Dictionary<PageFilterGroup> = {};
    let fileStatusFilter: FilterBuilder;
    let fileTypeFilter: FilterBuilder;
    if (committedFilter === false) {
      fileStatusFilter = new FilterBuilder({
        Id: 'file-status',
        Type: PageFilterGroupType.Select
      });
      fileStatusFilter.select('fileStatus', ['false']);
      allFilters['file-status'] = fileStatusFilter.value;
    }
    if (fileFilter) {
      fileTypeFilter = new FilterBuilder({
        Id: 'file-type',
        Type: PageFilterGroupType.Select
      });
      fileTypeFilter.select('fileType', [fileFilter]);
      allFilters['file-type'] = fileTypeFilter.value;
    }
    return allFilters;
  }

  getFilter$(name: string): Observable<PageFilterGroup> {
    return of(null);
  }

  getOrder(): PageSort {
    return null;
  }
  getOrder$(): Observable<PageSort> {
    return of(null);
  }
  setOrder$(orderBy: string, orderDir: SortDirection) {
    return of(null);
  }

}
